/* eslint-disable @typescript-eslint/indent */
import axios from 'api/apiCommon'
import baseAxios from 'axios'
import JSZip from 'jszip'
import { saveAs } from 'file-saver'

import { type GeneratedFile, GeneratedFileType } from './files.types'

const SIGNED_URL_EXPIRY_BUFFER = 2000

interface ProcessItemMetadata {
  programSlug: string
  participantId: string
  processItemSlug: string
  participantProcessItemId: string
}

interface CreateFileUrlPayload {
  type: GeneratedFileType
  mode?: 'r' | 'w' | 'rw'
  strict?: boolean
  multiple?: boolean
  data?: {
    programSlug?: string
    participantId?: string
    vendorId?: string
    proposalId?: string
    processItemSlug?: string
    participantProcessItemId?: string
    filename?: string
    key?: string
    groupId?: string
  }
}
export const createFileUrl = async ({
  type,
  mode = 'r',
  strict = false,
  multiple = true,
  data,
}: CreateFileUrlPayload): Promise<GeneratedFile> => {
  return await new Promise((resolve, reject) => {
    axios
      .post<GeneratedFile>(
        `files?type=${type}&mode=${mode}&strict=${strict.toString()}&multiple=${multiple.toString()}`,
        data ?? {},
      )
      .then((res) => {
        const file = { ...res }
        if (file.read) {
          file.read.created = Date.now()
        }
        if (file.write) {
          file.write.created = Date.now()
        }
        resolve(file)
      })
      .catch((err) => {
        console.error('err: ', err)
        reject(err)
      })
  })
}

interface GetFileUrlPayload {
  key: string
  mode?: 'r' | 'w' | 'rw'
}
export const getFileUrl = async ({ key, mode = 'r' }: GetFileUrlPayload): Promise<GeneratedFile> => {
  return await new Promise((resolve, reject) => {
    axios
      .get<GeneratedFile>(`files/${encodeURIComponent(key)}?mode=${mode}`)
      .then((res) => {
        const file = { ...res }
        if (file.read) {
          file.read.created = Date.now()
        }
        if (file.write) {
          file.write.created = Date.now()
        }
        resolve(file)
      })
      .catch((err) => {
        console.error('err: ', err)
        reject(err)
      })
  })
}

export const isSignedUrlExpired = (expiresIn: number, created: number): boolean => {
  if (!(expiresIn && created)) {
    return true
  }
  const expiresWithBuffer = expiresIn - SIGNED_URL_EXPIRY_BUFFER
  return created + expiresWithBuffer < Date.now()
}

export const downloadFile = async (
  type: GeneratedFileType,
  file: GeneratedFile,
  data?: Record<any, any>,
): Promise<undefined | GeneratedFile> => {
  const temporaryDownloadLink = document.createElement('a')
  temporaryDownloadLink.style.display = 'none'
  document.body.appendChild(temporaryDownloadLink)
  temporaryDownloadLink.setAttribute('download', file.filename)
  temporaryDownloadLink.setAttribute('target', '_blank')
  if (file.read && !isSignedUrlExpired(file.read.expiresIn, file.read.created)) {
    temporaryDownloadLink.setAttribute('href', file.read.url)
    // add timeout to allow download of multiple files
    // chrome still blocks the second file as a pop-up
    await new Promise<void>((resolve, reject) => {
      setTimeout(() => {
        temporaryDownloadLink.click()
        resolve()
      }, 550)
    })
  } else {
    const generatedFile = await getFileUrl({
      key: file.key,
      mode: 'r',
    })
    if (generatedFile) {
      temporaryDownloadLink.setAttribute('href', generatedFile.read?.url ?? '')
      temporaryDownloadLink.click()
      return generatedFile
    }
  }
}

export const downloadAll = async (
  type: GeneratedFileType,
  files: GeneratedFile[],
  folderName: string,
): Promise<void> => {
  try {
    const zip = new JSZip()
    const folder = zip.folder(folderName)
    const filePromises = files.map(async (file) => {
      if (file.read && !isSignedUrlExpired(file.read.expiresIn, file.read.created)) {
        return file
      } else {
        return await getFileUrl({
          key: file.key,
          mode: 'r',
        })
      }
    })
    const filesToDownload = await Promise.all(filePromises)
    const fileFetchPromises = filesToDownload.map(
      async (file) =>
        await baseAxios.get(file.read?.url ?? '', { responseType: 'blob' }).then((response) => ({
          fileName: file.filename,
          data: response.data,
        })),
    )
    const fetchedFiles = await Promise.all(fileFetchPromises)

    fetchedFiles.forEach(({ fileName, data }) => {
      if (folder) {
        folder.file(fileName, data, { binary: true })
      }
    })

    await zip.generateAsync({ type: 'blob' }).then((content) => {
      saveAs(content, `${folderName}.zip`)
      return null
    })
  } catch (e) {
    console.error('error downloading files: ', e)
    await Promise.reject(e)
  }
}

const fileToBlob = async (file: File): Promise<Blob> => {
  return new Blob([new Uint8Array(await file.arrayBuffer())], { type: file.type })
}

export const uploadFile = async (
  generatedFile: GeneratedFile,
  file: File,
  processItemMetadata?: ProcessItemMetadata,
  groupId?: string,
): Promise<any> => {
  let writeUrl = generatedFile.write?.url
  if (
    !generatedFile.write ||
    isSignedUrlExpired(generatedFile.write.expiresIn, generatedFile.write.created)
  ) {
    const { write } = await createFileUrl({
      type: GeneratedFileType.ProcessItemFile,
      mode: 'w',
      multiple: false,
      data: { ...processItemMetadata, filename: generatedFile.filename, groupId },
    })
    writeUrl = write?.url
  }
  const blob = await fileToBlob(file)
  return await baseAxios
    .put(writeUrl ?? '', blob, {
      headers: {
        'Content-Type': file.type,
      },
    })
    .catch((err) => {
      console.error('err: ', err)
      throw err
    })
}

export const uploadVendorFile = async (
  generatedFile: GeneratedFile,
  vendorId: string,
  file: File,
  processItemMetadata?: ProcessItemMetadata,
): Promise<any> => {
  let writeUrl = generatedFile.write?.url
  if (
    !generatedFile.write ||
    isSignedUrlExpired(generatedFile.write.expiresIn, generatedFile.write.created)
  ) {
    const { write } = await createFileUrl({
      type: GeneratedFileType.ProcessItemFile,
      mode: 'w',
      multiple: false,
      data: { vendorId, filename: generatedFile.filename },
    })
    writeUrl = write?.url
  }
  const blob = await fileToBlob(file)
  return await baseAxios
    .put(writeUrl ?? '', blob, {
      headers: {
        'Content-Type': file.type,
      },
    })
    .catch((err) => {
      console.error('err: ', err)
      throw err
    })
}

export const downloadPhoto = async (key: string): Promise<string | undefined> => {
  const generatedFile = await getFileUrl({
    key,
    mode: 'r',
  })
  if (generatedFile?.read?.url) {
    try {
      const response = await fetch(generatedFile.read.url)
      const blob = await response.blob()
      const base64Image: string = await new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onloadend = () => {
          if (typeof reader.result === 'string') {
            resolve(reader.result)
          } else {
            reject(new Error('Error converting image to base64.'))
          }
        }
        reader.readAsDataURL(blob)
      })
      return base64Image
    } catch (error) {
      console.error(error)
    }
  }
}

export const downloadHTML = async (url: string): Promise<string | undefined> => {
  try {
    const response = await fetch(url)
    const text = await response.text()
    const parser = new DOMParser()
    const doc = parser.parseFromString(text, 'text/html')
    const strippedContent = doc.body.innerHTML.replace(/<\/?div>/g, '')
    return strippedContent
  } catch (error) {
    console.error(error)
  }
}

export const uploadPhoto = async (
  uploadedFile: File,
  processItemMetadata: ProcessItemMetadata,
): Promise<GeneratedFile | undefined> => {
  try {
    const generatedFile = await createFileUrl({
      type: GeneratedFileType.ProcessItemFile,
      mode: 'rw',
      multiple: false,
      data: { ...processItemMetadata, filename: uploadedFile.name },
    })
    await uploadFile(generatedFile, uploadedFile, processItemMetadata)
    return generatedFile
  } catch (e) {
    console.error('error uploading files: ', e)
  }
}

export const uploadUserProfileImages = async (uploadedFile: File): Promise<GeneratedFile | undefined> => {
  try {
    const generatedFile = await createFileUrl({
      type: GeneratedFileType.UserProfileImages,
      mode: 'rw',
      data: { filename: uploadedFile.name },
    })
    await uploadFile(generatedFile, uploadedFile, undefined)
    return generatedFile
  } catch (e) {
    console.error('error uploading files: ', e)
  }
}
export const uploadProfilePhoto = async (
  uploadedFile: File,
  groupId: string,
): Promise<GeneratedFile | undefined> => {
  try {
    const generatedFile = await createFileUrl({
      type: GeneratedFileType.GroupProfileImages,
      mode: 'rw',
      data: { filename: uploadedFile.name, groupId },
    })
    await uploadFile(generatedFile, uploadedFile, undefined, groupId)
    return generatedFile
  } catch (e) {
    console.error('error uploading files: ', e)
  }
}
export const uploadVendorProfilePhoto = async (
  uploadedFile: File,
  vendorId: string,
): Promise<GeneratedFile | undefined> => {
  try {
    const generatedFile = await createFileUrl({
      type: GeneratedFileType.VendorLogo,
      mode: 'rw',
      data: { filename: uploadedFile.name, vendorId },
    })
    await uploadVendorFile(generatedFile, vendorId, uploadedFile)
    return generatedFile
  } catch (e) {
    console.error('error uploading files: ', e)
  }
}
export const uploadHTML = async (
  content: string,
  multiple: boolean = false,
  key: string | undefined,
  filename: string | undefined,
  generatedFile: GeneratedFile | null,
  processItemMetadata: ProcessItemMetadata,
): Promise<GeneratedFile | undefined | null> => {
  const htmlContent = `<!DOCTYPE html>
    <html>
      <head>
        <meta charset="UTF-8">
        <title>Generated HTML File</title>
      </head>
      <body>
        <div>${content}</div>
      </body>
    </html>`

  if (key && filename && generatedFile) {
    const file = new File([htmlContent], filename)
    await uploadFile(generatedFile, file, processItemMetadata)
    return undefined
  } else {
    const filename = `description${multiple ? Date.now() : ''}.html`
    const file = new File([htmlContent], filename)
    const generatedFile = await createFileUrl({
      type: GeneratedFileType.ProcessItemFile,
      mode: 'rw',
      multiple: false,
      data: { ...processItemMetadata, filename },
    }).catch(() => {
      return null
    })
    if (generatedFile) {
      await uploadFile(generatedFile, file, processItemMetadata)
    }
    return generatedFile
  }
}

export const initRTEUrl = async (
  multiple: boolean = false,
  key: string | undefined,
  processItemMetadata: ProcessItemMetadata,
): Promise<GeneratedFile | null> => {
  const filename = `${processItemMetadata.processItemSlug}-description${multiple ? Date.now() : ''}.html`
  return await createFileUrl({
    type: GeneratedFileType.ProcessItemFile,
    mode: 'rw',
    multiple: false,
    data: { ...processItemMetadata, filename },
  }).catch(() => {
    return null
  })
}
